import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePublicConfig } from "@/lib/usePublicConfig";
import { OFFERTE_TYPES } from "@/lib/branches";

const PROVINCES = [
  "Antwerpen", "Brussel", "Limburg", "Oost-Vlaanderen", "Vlaams-Brabant", "West-Vlaanderen",
  "Henegouwen", "Luik", "Luxemburg", "Namen", "Waals-Brabant",
  "Noord-Holland", "Zuid-Holland", "Utrecht", "Noord-Brabant", "Gelderland",
  "Overijssel", "Flevoland", "Friesland", "Groningen", "Drenthe", "Limburg (NL)", "Zeeland",
];

const formatEuro = (cents) => ((cents || 0) / 100).toFixed(2).replace(".", ",");

export default function VoorBedrijvenAanmelden() {
  const navigate = useNavigate();
  const config = usePublicConfig();
  const price = config?.b2b_monthly_price_cents ?? 2900;

  const [form, setForm] = useState({
    business_name: "",
    vat_number: "",
    contact_email: "",
    contact_phone: "",
    website: "",
    description: "",
    branches: [],
    regions: [],
    accept_terms: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const toggle = (field, value) => {
    setForm((f) => {
      const arr = f[field];
      return { ...f, [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const submit = async () => {
    if (!form.accept_terms) {
      toast.error("Aanvaard de voorwaarden om door te gaan.");
      return;
    }
    if (form.branches.length === 0 || form.regions.length === 0) {
      toast.error("Kies minstens één branche én één regio.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/businesses/register", form);
      window.location.href = data.payment_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aanmelden mislukt — controleer de velden.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <section className="oc-section">
        <div className="oc-container max-w-2xl">
          <h1 className="font-display font-bold text-[36px] text-oc-primary leading-tight">Word OfferteExpert partner</h1>
          <p className="text-oc-textSecondary text-[15px] mt-3">
            Vul je bedrijfsgegevens in. Na betaling van <strong>€ {formatEuro(price)}</strong> voor de eerste maand controleren we je aanvraag binnen 1 werkdag.
          </p>

          <div className="mt-8 space-y-5 bg-white rounded-oc border border-oc-border p-7">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bedrijfsnaam</Label>
                <Input data-testid="b2b-name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="h-12 rounded-oc-btn" />
              </div>
              <div className="space-y-2">
                <Label>BTW-nummer</Label>
                <Input data-testid="b2b-vat" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value.toUpperCase() })} placeholder="BE0123456789" className="h-12 rounded-oc-btn font-mono" />
              </div>
              <div className="space-y-2">
                <Label>Contact e-mail</Label>
                <Input data-testid="b2b-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="h-12 rounded-oc-btn" />
              </div>
              <div className="space-y-2">
                <Label>Telefoon (optioneel)</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="h-12 rounded-oc-btn" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Website</Label>
                <Input data-testid="b2b-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" className="h-12 rounded-oc-btn" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Beschrijving van je bedrijf</Label>
              <Textarea
                data-testid="b2b-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Wat doet je bedrijf, hoeveel ervaring heb je, welke USP's? (minimum 20 tekens)"
                className="min-h-[120px] rounded-oc-btn"
              />
            </div>

            <div className="space-y-2">
              <Label>Branches waarin je actief bent</Label>
              <div className="flex flex-wrap gap-2">
                {OFFERTE_TYPES.map((b) => (
                  <button
                    type="button"
                    key={b}
                    data-testid={`b2b-branche-${b}`}
                    onClick={() => toggle("branches", b)}
                    className={`px-4 py-2 rounded-oc-btn text-[13px] font-semibold border transition-colors ${
                      form.branches.includes(b)
                        ? "bg-oc-primary text-white border-oc-primary"
                        : "bg-white text-oc-textSecondary border-oc-border hover:bg-oc-subtle"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Regio's (provincies)</Label>
              <div className="flex flex-wrap gap-2">
                {PROVINCES.map((r) => (
                  <button
                    type="button"
                    key={r}
                    onClick={() => toggle("regions", r)}
                    className={`px-3 py-1.5 rounded-oc-btn text-[12px] font-semibold border transition-colors ${
                      form.regions.includes(r)
                        ? "bg-oc-secondary text-white border-oc-secondary"
                        : "bg-white text-oc-textSecondary border-oc-border hover:bg-oc-subtle"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-start gap-3 p-4 rounded-oc bg-oc-subtle border border-oc-border cursor-pointer">
              <Checkbox data-testid="b2b-accept-terms" checked={form.accept_terms} onCheckedChange={(v) => setForm({ ...form, accept_terms: !!v })} className="mt-0.5" />
              <span className="text-[13px] text-oc-textSecondary leading-relaxed">
                Ik aanvaard de <a href="/algemene-voorwaarden" target="_blank" className="text-oc-secondary underline">algemene voorwaarden</a> en het <a href="/privacy-policy" target="_blank" className="text-oc-secondary underline">privacybeleid</a>. Ik begrijp dat goedkeuring afhangt van een controle door OfferteExpert.
              </span>
            </label>

            <Button
              data-testid="b2b-submit"
              onClick={submit}
              disabled={submitting}
              className="w-full h-14 rounded-oc-btn bg-oc-primary hover:bg-oc-secondary text-white font-semibold text-[15px]"
            >
              {submitting ? "Doorsturen..." : `Aanmelden en € ${formatEuro(price)} betalen`}
            </Button>
            <p className="text-[12px] text-oc-textSecondary text-center">Je wordt doorgestuurd naar MultiSafepay voor de eerste maandbetaling.</p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
